import { faArrowRight, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState, version, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import AppBar from "../../common/AppBar";
import Snackbar from "../../common/Snackbar";
import SearchBarWithFilter from "../../components/SearchBarWithFilter";
import { getAllFormTemplates } from "../../redux/formTemplateSlice";
import { createSectionForm } from "../../redux/sectionFormSlice";
import { generateSHA256 } from "../../util/generateSHA256";
import { buildPermissionContext } from "../../util/permissions";
import ShortDescriptionEditor from "../../common/RichTextEditor/ShortDescriptionEditor";

const modalColumnNames = ["Form Name"];

const columnMap = {
  "Form Name": "form_name",
};

const fieldTypes = [
  "Text",
  "Number",
  "Radio",
  "Checkbox",
  "Radio Dropdown",
  "Checkbox Dropdown",
];

const validationRules = {
  Text: [
    "None",
    "Character limit less then",
    "Character limit more then",
    "Character limit between",
    "Required keywords",
  ],
  Number: ["None", "More than", "Less than", "Between"],
};

const SectionForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authUser = useSelector((state) => state.auth.user);
  const permissionCtx = buildPermissionContext(authUser);
  const canEdit = permissionCtx.permissions?.Edit === true;
  const {
    sectionId,
    stepId,
    revisionId,
    partId,
    partNumber,
    section,
    elementTitle,
    stepTitle,
    revision,
    substepId,
    siblingOrder,
  } = useParams();
  const [rows, setRows] = useState([]);
  const [editingRowId, setEditingRowId] = useState(null);
  const [editedRowData, setEditedRowData] = useState({});
  const [searchQuery, setSearchQuery] = useState(""); // For filtering parts
  const [newRow, setNewRow] = useState({});
  const [comment, setComment] = useState(""); // For filtering parts
  
  // Simple unsaved changes tracking (for all fields except short description)
  const [hasChanges, setHasChangesState] = useState(false);
  const hasChangesRef = useRef(false);
  const setHasChanges = (val) => {
    hasChangesRef.current = val;
    setHasChangesState(val);
  };
  
  // Track initial state
  const initialStateRef = useRef({
    templateName: "",
    shortDescription: "",
    rows: [],
    comment: ""
  });
  
  // Track if user has made any actual changes (non-short-description fields)
  const [userHasInteracted, setUserHasInteractedState] = useState(false);
  const userHasInteractedRef = useRef(false);
  const setUserHasInteracted = (val) => {
    userHasInteractedRef.current = val;
    setUserHasInteractedState(val);
  };

  // Undo-based dirty tracking for the Lexical short-description editor.
  // Fires with `true` when the editor has undo history (real user edit) and
  // `false` when the user undoes everything back to the original state.
  const [isShortDescriptionDirty, setIsShortDescriptionDirty] = useState(false);
  const isShortDescriptionDirtyRef = useRef(false);
  const handleShortDescriptionDirtyChange = (dirty) => {
    isShortDescriptionDirtyRef.current = dirty;
    setIsShortDescriptionDirty(dirty);
  };

  const handleAddRow = () => {
    setUserHasInteracted(true);
    setHasChanges(true);
    
    const newId = Date.now();
    const newRowData = {
      id: newId,
      position: newRow.position || rows.length + 1,
      type: newRow.type,
      title: newRow.title,
      options:
        newRow.type === "Radio" || newRow.type === "Checkbox" ? [""] : [],
      validation: "",
      graded: false,
      mustBeCorrect: false,
      value: "",
    };
    setRows((prev) => [...prev, newRowData]);
    setEditingRowId(rows.length - 1);
    setNewRow({});
  };

  const handleSaveEdit = (id) => {
    setUserHasInteracted(true);
    setHasChanges(true);
    
    setRows((prevRows) =>
      prevRows.map((row) =>
        row.id === id ? { ...row, ...editedRowData } : row
      )
    );
    setEditingRowId(null);
    setEditedRowData({});
  };

  const addOption = (index) => {
    setUserHasInteracted(true);
    setHasChanges(true);
    
    const updatedRows = [...rows];
    if (!updatedRows[index].options) updatedRows[index].options = [];
    updatedRows[index].options.push("");
    setRows(updatedRows);
  };

  const removeOption = (index, optIndex) => {
    setUserHasInteracted(true);
    setHasChanges(true);
    
    const updatedRows = [...rows];
    updatedRows[index].options.splice(optIndex, 1);
    setRows(updatedRows);
  };

  const handleCheckboxChange = (index, key) => {
    setUserHasInteracted(true);
    setHasChanges(true);
    
    const updatedRows = [...rows];
    updatedRows[index][key] = !updatedRows[index][key];
    setRows(updatedRows);
  };

  const handleDelete = (id) => {
    setUserHasInteracted(true);
    setHasChanges(true);
    
    setRows((prev) => prev.filter((row) => row.id !== id));
  };

  const handleSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const [showModal, setShowModal] = useState(false);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "",
  });
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: null,
  });
  const [shortDescription, setShortDescription] = useState("");
  const [formSchema, setFormSchema] = useState(null);
  const [formFields, setFormFields] = useState([]);
  const [uiSchema, setUiSchema] = useState({});
  const [templateName, setTemplateName] = useState("");
  const [templateId, setTemplateId] = useState("");
  const { formTemplates } = useSelector((state) => state.formTemplates);
  const handleSaveClick = () => setShowModal(true);
  const handleCloseModal = () => {
    setShowModal(false);
    // Don't reset anything when user closes modal without saving
  };
  const [searchType, setSearchType] = useState("all_columns");
  const [selectedColumns, setSelectedColumns] = useState({
    form_name: true,
  });
  const breadcrumb_text = `${partNumber} > Rev ${revision} > ${section} > ${stepTitle} > ${elementTitle}`;
  const pageTitle = elementTitle;

  const handleSelectTemplate = async (selectedTemplate) => {
    setUserHasInteracted(true);
    setHasChanges(true);
    
    setTemplateName(selectedTemplate.form_name);
    setTemplateId(selectedTemplate.id);
    setFormSchema(selectedTemplate.json_schema);
    setUiSchema(selectedTemplate.ui_schema);

    // Flatten and parse form fields from JSON Schema
    const fields = [];
    const parseSchema = (properties, path = "") => {
      Object.entries(properties).forEach(([key, value], index) => {
        const fullPath = path ? `${path}.${key}` : key;

        if (value.type === "object") {
          parseSchema(value.properties, fullPath);
        } else {
          fields.push({
            position: fields.length + 1,
            key: fullPath,
            type: value.type,
            title: key,
            description: value.description || "",
            validation: value.pattern || value.format || "",
            graded: false,
            mustBeCorrect: false,
            options: value.enum || [], // if available
            value: value.default || "", // default value if any
          });
        }
      });
    };

    parseSchema(selectedTemplate.json_schema.properties);
    setFormFields(fields);
    setTemplateModalOpen(false);
  };

  const handleConfirmSave = async () => {
    if (!comment) {
      showSnackbar("Comment is mandatory.", "error");
      return;
    }
    const hash = await generateSHA256(Math.random().toString());

    const form_data = {
      schema: formSchema,
      fields: formFields,
      uiSchema,
    };

    const sectionFormData = {
      form_name: formSchema?.title || "Untitled Form",
      short_description: shortDescription,
      revision_id_ref: revisionId,
      section_id_ref: sectionId,
      step_id_ref: stepId,
      part_required_id_ref: partId,
      substep_id_ref:
        substepId === "null" || substepId === "undefined" ? "" : substepId,
      form_template_ref: templateId,
      form_data,
      sibling_order: (() => {
        if (!siblingOrder || siblingOrder === "null") return null;
        return siblingOrder; // fallback
      })(),
    };

    try {
      await dispatch(
        createSectionForm({
          sectionFormData,
          version_content: {
            ...sectionFormData,
            has_audit_trail_preview: true,
            action_to_show: `Save ${elementTitle} for ${partNumber}, rev ${revision}`,
            partNumber,
            revision,
            section,
            stepTitle,
            elementTitle,
            breadcrumb_text,
            pageTitle,

            // New keys for manage preview in move and copy.
            sibling_order: siblingOrder,
            type: "form",
            part_id_ref: partId,
            procedure_rev_ref: revisionId,
            section_tree_node_id: sectionId,
            step_tree_node_id: stepId,
            sub_step_tree_node_id: substepId,
          },
        })
      ).unwrap();
      showSnackbar("Form created successfully!", "success");
    } catch (error) {
      showSnackbar("Error creating Form: " + error?.message, "error");
    }

    setShowModal(false);
    // Reset changes after successful save
    setHasChanges(false);
    setUserHasInteracted(false);
    navigate(-1);
  };
  const showSnackbar = (message, type) => {
    setSnackbar({ open: true, message, type });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Simple change handlers
  const handleCommentChange = (value) => {
    setUserHasInteracted(true);
    setHasChanges(true);
    setComment(value);
  };

  const handleShortDescriptionChange = (value) => {
    // NOTE: setUserHasInteracted / setHasChanges are intentionally NOT called here.
    // Dirty detection for short description is driven by the Lexical undo history
    // (handleShortDescriptionDirtyChange) to avoid false positives from the
    // editor's own programmatic initialisation onChange event.
    setShortDescription(value);
  };

  // Handle back navigation with unsaved changes check
  const handleBackNavigation = () => {
    const hasAnyChanges =
      isShortDescriptionDirtyRef.current ||
      (userHasInteractedRef.current && hasChangesRef.current);

    if (hasAnyChanges) {
      const confirmed = window.confirm("You have unsaved changes. Are you sure you want to leave?");
      if (!confirmed) {
        return; // Don't navigate
      }
    }
    navigate(-1); // Navigate if confirmed or no changes
  };

  return (
    <div className="section-form">
      <AppBar
        subTitleAboveTitle={
          <>{localStorage.getItem("user_name")} is Creating</>
        }
        title={elementTitle}
        showTablesDropdown={true}
        onBack={handleBackNavigation}
        buttons={[
          { label: "Preview", path: "/preview" },
          {
            label: "Save",
            onClick: canEdit ? handleSaveClick : undefined,
            disabled: !canEdit,
            title: !canEdit
              ? "You do not have permission to save forms."
              : undefined,
          },
        ]}
        subheading={`${partNumber} > Rev ${revision} > ${section} > ${stepTitle} > ${elementTitle}`}
        showBlackTitle={true}
      />
      {/* Form Meta Info */}
      <div className="p-6">
        <div className="form-group">
          <label>ID (Read Only)</label>
          <input type="text" name="id" readOnly value={""} />
        </div>

        <div className="form-group mt-4">
          <label className="block font-semibold">Select Form</label>
          <div className="input-with-icon">
            <input
              type="text"
              value={templateName}
              placeholder="Form Template"
              readOnly
            />
            <button
              type="button"
              className="edit-icon"
              onClick={() => setTemplateModalOpen(true)}
            >
              ✎
            </button>
          </div>
          {/* <input
            type="file"
            accept=".json"
            className="w-full border p-2 mt-2"
            onChange={handleSchemaUpload}
          /> */}
        </div>

        <div className="form-group mt-4" style={{ marginLeft: "10px", marginRight: "10px" }}>
          <label className="block font-semibold">Short Description</label>
          <div className="mt-2">
            <ShortDescriptionEditor
              value={shortDescription}
              onChange={handleShortDescriptionChange}
              onDirtyChange={handleShortDescriptionDirtyChange}
              placeholder="Enter a short description"
              minHeight="100px"
            />
          </div>
        </div>
      </div>
      {/* Form Fields Table */}
      <div className="p-6 mt-4">
        <h3 className="text-lg font-semibold mb-2">Form Fields</h3>
        {true ? (
          <table className="table" style={{ marginTop: "-13px" }}>
            <thead>
              <tr>
                <th></th>
                <th>Type</th>
                <th>Label Description</th>
                <th>Options</th>
                <th>Validation Rule</th>
                <th>Graded?</th>
                <th>Must Be Correct?</th>
                <th>Value</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={row.id}>
                  {editingRowId === row.id ? (
                    <>
                      <td>
                        <input
                          type="number"
                          value={editedRowData.position || ""}
                          onChange={(e) =>
                            setEditedRowData({
                              ...editedRowData,
                              position: e.target.value,
                            })
                          }
                        />
                      </td>
                      <td>
                        <select
                          value={editedRowData.type || ""}
                          onChange={(e) =>
                            setEditedRowData({
                              ...editedRowData,
                              type: e.target.value,
                              options:
                                e.target.value === "Radio" ||
                                e.target.value === "Checkbox"
                                  ? [""]
                                  : [],
                            })
                          }
                        >
                          <option value="">Select</option>
                          {fieldTypes.map((type) => (
                            <option key={type}>{type}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          type="text"
                          value={editedRowData.title || ""}
                          onChange={(e) =>
                            setEditedRowData({
                              ...editedRowData,
                              title: e.target.value,
                            })
                          }
                        />
                      </td>
                      <td>
                        {(editedRowData.type === "Radio" ||
                          editedRowData.type === "Checkbox") &&
                          editedRowData.options?.map((opt, optIndex) => (
                            <div
                              key={optIndex}
                              className="flex items-center mb-1"
                            >
                              <input
                                type="text"
                                value={opt}
                                onChange={(e) => {
                                  const updatedOptions = [
                                    ...editedRowData.options,
                                  ];
                                  updatedOptions[optIndex] = e.target.value;
                                  setEditedRowData({
                                    ...editedRowData,
                                    options: updatedOptions,
                                  });
                                }}
                                className="mr-2"
                              />
                              <button
                                onClick={() => {
                                  const updatedOptions = [
                                    ...editedRowData.options,
                                  ];
                                  updatedOptions.splice(optIndex, 1);
                                  setEditedRowData({
                                    ...editedRowData,
                                    options: updatedOptions,
                                  });
                                }}
                              >
                                ❌
                              </button>
                            </div>
                          ))}

                        {(editedRowData.type === "Radio" ||
                          editedRowData.type === "Checkbox") && (
                          <button
                            onClick={() =>
                              setEditedRowData({
                                ...editedRowData,
                                options: [...(editedRowData.options || []), ""],
                              })
                            }
                          >
                            {editedRowData.type === "Radio"
                              ? "+ Add Radio"
                              : "+ Add Checkbox"}
                          </button>
                        )}
                      </td>

                      <td>
                        {["Text", "Number"].includes(editedRowData.type) ? (
                          <select
                            value={editedRowData.validation || ""}
                            onChange={(e) =>
                              setEditedRowData({
                                ...editedRowData,
                                validation: e.target.value,
                              })
                            }
                          >
                            <option value="">Select</option>
                            {validationRules[editedRowData.type].map((rule) => (
                              <option key={rule} value={rule}>
                                {rule}
                              </option>
                            ))}
                          </select>
                        ) : (
                          "-"
                        )}
                      </td>

                      <td>
                        <label>
                          <input
                            type="checkbox"
                            checked={editedRowData.graded || false}
                            onChange={() =>
                              setEditedRowData({
                                ...editedRowData,
                                graded: !editedRowData.graded,
                              })
                            }
                          />{" "}
                          Yes
                        </label>
                      </td>
                      <td>
                        <label>
                          <input
                            type="checkbox"
                            checked={editedRowData.mustBeCorrect || false}
                            onChange={() =>
                              setEditedRowData({
                                ...editedRowData,
                                mustBeCorrect: !editedRowData.mustBeCorrect,
                              })
                            }
                          />{" "}
                          Yes
                        </label>
                      </td>

                      <td>
                        <input
                          type="text"
                          value={editedRowData.value || ""}
                          onChange={(e) =>
                            setEditedRowData({
                              ...editedRowData,
                              value: e.target.value,
                            })
                          }
                        />
                      </td>

                      <td>
                        <button onClick={() => handleSaveEdit(row.id)}>
                          ✔
                        </button>
                        <button
                          onClick={() => {
                            setEditingRowId(null);
                            setEditedRowData({});
                          }}
                        >
                          ✕
                        </button>
                      </td>
                    </>
                  ) : (
                    <>
                      {/* Readonly Row */}
                      <td>{row.position}</td>
                      <td>{row.type}</td>
                      <td>{row.title}</td>
                      <td>
                        {(row.type === "Radio" || row.type === "Checkbox") &&
                          row.options?.join(", ")}
                      </td>
                      <td>{row.validation}</td>
                      <td>
                        <input type="checkbox" checked={row.graded} disabled />
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          checked={row.mustBeCorrect}
                          disabled
                        />
                      </td>
                      <td>{row.value}</td>
                      <td>
                        <button
                          onClick={() => {
                            setEditingRowId(row.id);
                            setEditedRowData(row);
                          }}
                        >
                          ✎
                        </button>
                        <button onClick={() => handleDelete(row.id)}>🗑</button>
                      </td>
                    </>
                  )}
                </tr>
              ))}

              <tr>
                <td>
                  <input
                    type="number"
                    value={newRow.position || ""}
                    onChange={(e) =>
                      setNewRow({ ...newRow, position: e.target.value })
                    }
                  />
                </td>
                <td>
                  <select
                    value={newRow.type || ""}
                    onChange={(e) =>
                      setNewRow({ ...newRow, type: e.target.value })
                    }
                  >
                    <option value="">Select</option>
                    {fieldTypes.map((type) => (
                      <option key={type}>{type}</option>
                    ))}
                  </select>
                </td>
                <td>
                  <input
                    type="text"
                    value={newRow.title || ""}
                    onChange={(e) =>
                      setNewRow({ ...newRow, title: e.target.value })
                    }
                  />
                </td>
                <td colSpan="5"></td>
                <td></td>
              </tr>
            </tbody>
          </table>
        ) : (
          <p className="text-gray-500">
            No fields found. Upload a schema to continue.
          </p>
        )}
      </div>

      <button onClick={handleAddRow} className="add-row-button">
        + Add Row
      </button>

      {templateModalOpen && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-content_forpart">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "10px",
                  position: "relative",
                }}
              >
                <h3
                  style={{
                    textAlign: "center",
                    margin: 0,
                    position: "absolute",
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: "100%",
                  }}
                >
                  Form Templates
                </h3>
                <button
                  onClick={() => setTemplateModalOpen(false)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontSize: "20px",
                    color: "#666",
                    marginLeft: "auto",
                    zIndex: 1,
                  }}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <div className="search-container" style={{ marginTop: "-30px" }}>
                <SearchBarWithFilter
                  searchQuery={searchQuery}
                  setSearchQuery={setSearchQuery}
                  columnOptions={[
                    { id: "name", label: "Name" },
                    { id: "orders", label: "Order" },
                    { id: "short_description", label: "Short Description" },
                    { id: "module", label: "Module?" },
                    { id: "procedure", label: "Document?" },
                    { id: "flowchart", label: "Flowchart?" },
                  ]}
                  searchType={searchType}
                  setSearchType={setSearchType}
                  selectedColumns={selectedColumns}
                  setSelectedColumns={setSelectedColumns}
                />
              </div>

              <table className="table tablecustom">
                <thead>
                  <tr>
                    {modalColumnNames.map((col) => {
                      const colKey = columnMap[col];
                      const isSortable = colKey !== null;

                      return (
                        <th
                          key={col}
                          onClick={() => isSortable && handleSort(colKey)}
                          className={`sortable-header ${
                            !isSortable ? "no-sort" : ""
                          }`}
                          style={col === "Action" ? { width: "10%" } : {}}
                        >
                          <div className="header-content">
                            <span>
                              {col}
                              {(col === "Name" || col === "Order") && (
                                <span
                                  style={{
                                    color: "#0D6EFD",
                                    marginLeft: "2px",
                                  }}
                                >
                                  *
                                </span>
                              )}
                            </span>
                            {isSortable && (
                              <div className="sort-icons">
                                <span
                                  className={`triangle-up ${
                                    sortConfig.key === colKey &&
                                    sortConfig.direction === "ascending"
                                      ? "active"
                                      : ""
                                  }`}
                                ></span>
                                <span
                                  className={`triangle-down ${
                                    sortConfig.key === colKey &&
                                    sortConfig.direction === "descending"
                                      ? "active"
                                      : ""
                                  }`}
                                ></span>
                              </div>
                            )}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {formTemplates
                    .filter(
                      (template) =>
                        typeof template?.form_name === "string" &&
                        template.form_name
                          .toLowerCase()
                          .includes(searchQuery.toLowerCase())
                    )
                    .map((row) => (
                      <tr key={row.id}>
                        <td>{row.form_name}</td>

                        <td className="action-buttons">
                          <button
                            className="right-arrow"
                            onClick={() => handleSelectTemplate(row)}
                          >
                            <FontAwesomeIcon icon={faArrowRight} />
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>

              <br />
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Save {elementTitle}</h2>
            <hr className="divider" />
            <h3>
              For {partNumber} &gt; Rev {revision} &gt; {section} &gt;{" "}
              {stepTitle} &gt; {elementTitle}
            </h3>

            <p>
              <strong>Editor:</strong> {localStorage.getItem("user_name")}
            </p>

            <label className="modal-label">
              Comment<span className="required">*</span>
            </label>
            <textarea
              className="modal-textarea"
              placeholder="Enter your comments here..."
              value={comment}
              onChange={(e) => handleCommentChange(e.target.value)}
              style={{}}
            ></textarea>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
                marginTop: "20px",
              }}
            >
              <button
                onClick={handleCloseModal}
                style={{
                  padding: "8px 16px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  background: "#6c757d",
                  color: "#e9ecef",
                  cursor: "pointer",
                }}
              >
                Close
              </button>
              <button
                onClick={handleConfirmSave}
                style={{
                  padding: "8px 16px",
                  border: "1px solid #007bff",
                  borderRadius: "4px",
                  background: "#007bff",
                  color: "white",
                  cursor: "pointer",
                  marginRight: "7px",
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
      {snackbar.open && (
        <Snackbar
          message={snackbar.message}
          type={snackbar.type}
          onClose={handleCloseSnackbar}
        />
      )}
    </div>
  );
};

export default SectionForm;
